/**
 * Sanitasi pesan error agar tidak bocor ke toast user/creator/kasir.
 * Detail teknis dikirim ke backend → laravel.log.
 */

const FRIENDLY = [
  'Sedang ada gangguan sementara. Silakan coba lagi sebentar ya.',
  'Sistem lagi sibuk sejenak. Mohon tunggu sebentar lalu coba kembali.',
  'Belum bisa dilanjutkan saat ini. Coba beberapa saat lagi ya.',
];

const TECH_HINTS = [
  'syntax error',
  'unexpected token',
  'expecting "',
  'expecting function',
  'parse error',
  'fatal error',
  'uncaught',
  'typeerror',
  'referenceerror',
  'sqlstate',
  'illuminate\\',
  'vendor/',
  'stack trace',
  'call to undefined',
  'undefined variable',
  'undefined index',
  'undefined array key',
  'failed opening',
  'failed to open stream',
  'on line ',
  '.php',
  'exception',
  'vite',
  'esbuild',
  'webpack',
  'node_modules',
  'econnrefused',
  'network error',
  'at object.',
  'cannot read prop',
  'is not a function',
  'is not defined',
  'module not found',
];

function pickFriendly() {
  return FRIENDLY[Math.floor(Math.random() * FRIENDLY.length)];
}

export function looksTechnical(message) {
  if (message == null) return true;
  const text = String(message).trim();
  if (!text) return true;
  if (text.length > 220) return true;

  const lower = text.toLowerCase();
  if (TECH_HINTS.some((h) => lower.includes(h))) return true;
  if (/[\\/](app|vendor|resources|node_modules)[\\/]/i.test(text)) return true;
  if (/\b(error|exception|trace)\b/i.test(text) && /[{}<>]|::|\\\\/.test(text)) return true;

  return false;
}

/**
 * @param {unknown} input - string | Error | axios error
 * @param {string} [fallback]
 */
export function toUserFacingMessage(input, fallback = pickFriendly()) {
  let candidate = '';

  if (typeof input === 'string') {
    candidate = input;
  } else if (input && typeof input === 'object') {
    const err = /** @type {any} */ (input);
    candidate =
      err.response?.data?.message ||
      (Array.isArray(err.response?.data?.errors)
        ? err.response.data.errors[0]
        : null) ||
      (err.response?.data?.errors &&
        Object.values(err.response.data.errors).flat?.()?.[0]) ||
      err.message ||
      '';
  }

  candidate = String(candidate || '').trim();

  // Validation 422: pesan field biasanya aman & berguna
  const status = input?.response?.status;
  if (status === 422 && candidate && !looksTechnical(candidate)) {
    return candidate;
  }

  if (status === 401 || status === 403) {
    if (candidate && !looksTechnical(candidate)) return candidate;
    return status === 401
      ? 'Sesi Anda berakhir. Silakan masuk kembali.'
      : 'Anda tidak memiliki akses untuk aksi ini.';
  }

  if (!candidate || looksTechnical(candidate)) {
    return fallback || pickFriendly();
  }

  return candidate;
}

/**
 * Kirim detail ke laravel.log via API (fire-and-forget).
 * @param {{ message?: string, source?: string, stack?: string, extra?: object }} payload
 */
export function reportClientError(payload = {}) {
  try {
    const body = JSON.stringify({
      message: String(payload.message || '').slice(0, 2000),
      source: payload.source || 'browser',
      url: typeof window !== 'undefined' ? window.location.href : null,
      stack: payload.stack ? String(payload.stack).slice(0, 8000) : null,
      extra: payload.extra || null,
    });

    // /api/* (bukan /api/v1) — tanpa CSRF, aman untuk sendBeacon
    const endpoint = '/api/client-errors';

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    // ignore
  }
}

/**
 * Ambil pesan aman + laporkan detail mentah bila teknis.
 */
export function resolveAndReportError(input, fallback) {
  const raw =
    typeof input === 'string'
      ? input
      : input?.response?.data?.message || input?.message || '';

  const safe = toUserFacingMessage(input, fallback);

  if (looksTechnical(raw) || (input?.response?.status >= 500)) {
    reportClientError({
      message: raw || String(input?.message || 'unknown'),
      source: 'web_client',
      stack: input?.stack || input?.response?.data?.exception || null,
      extra: {
        status: input?.response?.status,
        url: input?.config?.url,
        exception: input?.response?.data?.exception,
        file: input?.response?.data?.file,
        line: input?.response?.data?.line,
      },
    });
  }

  return safe;
}

export default { looksTechnical, toUserFacingMessage, reportClientError, resolveAndReportError };
